import * as moment from 'moment';
import brnv from 'bank-routing-number-validator';
import { Validator } from 'vee-validate';
import { SignUpService } from './common/services/sign-up.service';
import { SchoolSettingsService } from './common/services/settings/school-settings.service';
import { UserData } from './common/auth/user-data';

const dictionary = {
    en: {
        messages: {
            required: () => 'This field is required',
            email: () => 'This field should be a valid email',
            confirmed: () => '“Password” and “Confirm Password” doesn’t match',
            numeric: () => 'May contain numbers only',
            min: (name, args) => `Length should be at least ${args} characters`,
            max: (name, args) => `Length should be less than ${args}`,
            credit_card: () => 'You should provide the valid card number',
            decimal: (name, args) => `May contain number with ${args} decimal points`
        }
    },
};

Validator.localize(dictionary);


const latinValidator = {
    getMessage() {
        return 'May contain latin letters, spaces, “‘“, “-”';
    },
    validate(value) {
        return /^[a-zA-Z '\-]*$/.test(value);
    }
};

const checkLatinSymbolValidator = {
    getMessage() {
        return 'May contain at least one latin letter';
    },
    validate(value) {
        return /[a-zA-Z]/.test(value);
    }
};

const passwordValidator = {
    getMessage() {
        return 'Password requires combination of 8 mixed case letters and numbers.';
    },
    validate(value) {
        return /^(?=.*[0-9])(?=.*[a-z])(?=.*[A-Z])(?=\S+$).*$/.test(value);
    }
};

const schoolNameValidator = {
    getMessage() {
        return 'May contain latin letters, numbers, spaces and punctuation marks';
    },
    validate(value) {
        return /^[a-zA-Z0-9 .,?!':;/@#$()"\-]+$/.test(value);
    }
};

const websiteValidator = {
    getMessage() {
        return 'Should start with http:// or https://';
    },
    validate(value) {
        return value.startsWith('http://') || value.startsWith('https://');
    }
};

const streetValidator = {
    getMessage() {
        return 'May contain latin letters, numbers, spaces and punctuation marks';
    },
    validate(value) {
        return /^[a-zA-Z0-9.,?!' :;/@#$()"\-]+$/.test(value);
    }
};

const addressValidator = {
    getMessage() {
        return 'May contain latin letters, numbers and spaces';
    },
    validate(value) {
        return /^[a-zA-Z0-9 ]+$/.test(value);
    }
};

const taglineValidator = {
    getMessage() {
        return 'May contain latin letters, whitespaces, numbers and punctuation marks';
    },
    validate(value) {
        return !/[^a-zA-Z0-9.,#№<>|;+= _`~^"\t`'’!?:/@$%&*(){}/\\\-]+/.test(value);
    }
};

const customFormsValidator = {
    getMessage() {
        return 'The following symbols are not allowed: ^=+-*$^:/)(';
    },
    validate(value) {
        return /^[^=+\-*$^:/)()]+$/.test(value);
    }
};

const timeZoneValidator = {
    getMessage() {
        return 'May contain latin letters, \'/\' and numbers only';
    },
    validate(value) {
        return /^[a-zA-Z0-9/]+/.test(value);
    }
};

const phoneValidator = {
    getMessage() {
        return 'You should fill the mask';
    },
    validate(value) {
        return /^[0-9]{3}[ ]{2}[0-9]{3}[ ]{2}[0-9]{4}/.test(value);
    }
};

const phoneValidatorForEngland = {
    getMessage() {
        return 'You should fill the mask';
    },
    validate(value) {
        return /^[0-9]{4}[ ]{2}[0-9]{3}[ ]{2}[0-9]{4}/.test(value);
    }
};

const uniqueEmailValidator = {
    getMessage() {
        return 'This email already exist';
    },
    async validate(value) {
        let validationResult;
        try {
            const { data } = await SignUpService.checkEmail(value);
            validationResult = !data;
        } catch (e) {
            validationResult = true;
        }
        return validationResult;
    }
};

const expirationDate = {
    getMessage() {
        return 'You should provide the valid expiration date';
    },
    validate(value) {
        return value.match(/\d{2}\/\d{2}/);
    }
};

const routingNumber = {
    getMessage() {
        return 'Routing number is invalid';
    },
    validate(value) {
        return brnv.ABARoutingNumberIsValid(value);
    }
};

const AccountNumberValidator = {
    getMessage() {
        return 'Account number is invalid';
    },
    validate(value) {
        return /^[0-9\-]+$/.test(value);
    }
};

const ExpiryYearValidator = {
    getMessage() {
        return 'Your card\'s expiration year is in the past.';
    },
    validate(value) {
        const currentDate = new Date();
        const enteredDate = new Date();
        const month = Number(value.substring(0, 2));
        const year = 2000 + Number(value.substring(2, 4));
        enteredDate.setMonth(month - 1);
        enteredDate.setFullYear(year);
        return currentDate <= enteredDate;
    }
};

const ExpiryMonthValidator = {
    getMessage() {
        return 'Your card\'s expiration month must be between 1-12 and not expired';
    },
    validate(value) {
        const month = Number(value.substring(0, 2));
        
        // Check if month is between 1 and 12
        if (month < 1 || month > 12) {
            return false;
        }
        
        // Check if the expiration date is not in the past
        const year = 2000 + Number(value.substring(2, 4));
        const currentDate = new Date();
        const enteredDate = new Date();
        enteredDate.setMonth(month - 1); 
        enteredDate.setFullYear(year);
        
        return currentDate <= enteredDate;
    }
};

const ExpiryDateLengthValidator = {
    getMessage() {
        return 'Your card\'s expiration date is incomplete.';
    },
    validate(value) {
        return value.length === 4;
    }
};

const PolicyValidator = {
    getMessage() {
        return 'You should use latin symbols and numbers';
    },
    validate(value) {
        return !/[^a-zA-Z0-9=.,#<>;_+{}`"\t`’\s&nbsp;'!?:*/@$()\\\ufeff-]+/.test(value);
    }
};

const ListItemValidator = {
    getMessage() {
        return 'You should use latin symbols and numbers';
    },
    validate(value) {
        return !/[^a-zA-Z0-9.,#№<>|;+=_`~^"`’﻿'!?:/@$%&*(){}\\\ufeff -]+/.test(value);
    }
};

const PortalNameValidator = {
    getMessage() {
        return 'You should use latin symbols and "-"';
    },
    validate(value) {
        return !/[^a-zA-Z0-9\\-]+/.test(value);
    }
};

const PortalNameUniqueValidator = {
    getMessage() {
        return 'This URL already exists';
    },
    async validate(value) {
        const { data } = await SchoolSettingsService.checkPortalName(UserData.getUserDataFromLocalStorage().schoolId, value);
        return !data;
    }
};

const ClassNameValidator = {
    getMessage() {
        return 'May contain latin letters, numbers, \".\", \",\" or \"-\" only';
    },
    validate(value) {
        return /^[0-9a-zA-z., \-]+$/.test(value);
    }
};

const ClassDescriptionValidator = {
    getMessage() {
        return 'May contain latin letters, numbers, ".", "," or "-" only';
    },
    validate(value) {
        return /^[0-9a-zA-z.,!?: \-]+$/.test(value);
    }
};

const ClassAddOnValidator = {
    getMessage() {
        return 'May contain latin letters, numbers, \".\", \",\" or \"-\" only';
    },
    validate(value) {
        return /^[0-9a-zA-z., \-]+$/.test(value);
    }
};

const FutureDateValidator = {
    getMessage() {
        return 'Should be future date';
    },
    validate(value) {
        return moment(value).isAfter(moment(), 'days');
    }
};

const CurrentOrFutureDateValidator = {
    getMessage() {
        return 'Should be current or future date';
    },
    validate(value) {
        return moment(value).isSameOrAfter(moment(), 'days');
    }
};

const PercentageValidator = {
    getMessage() {
        return 'Should be less or equal 100%';
    },
    validate(value) {
        const parts = String(value).split('.');
        const integerPart = parts[0];
        return integerPart <= 100 && integerPart >= 0;
    }
};

const BiggerNumberValidator = {
    getMessage(value, [biggerThen]) {
        return `Should be more then ${biggerThen}`;
    },
    validate(value, args) {
        const biggerThen = +args;
        return +value > biggerThen;
    }
};

const BiggerOrEqualNumberValidator = {
    getMessage(value, [biggerOrEqualThen]) {
        return biggerOrEqualThen !== 0 ? `Should be more or equal then ${biggerOrEqualThen}` : 'Should be positive';
    },
    validate(value, args) {
        const biggerOrEqualThen = +args;
        return +value >= biggerOrEqualThen;
    }
};

const TimeBeforeValidator = {
    getMessage() {
        return 'Start time should be before end time';
    },
    validate(value, args) {
        if (args[0].length < 2) return true;

        const [hours, minutes] = value.split(':');
        const [otherHours, otherMinutes] = args[0].split(':');

        if ([otherHours, otherMinutes].some(item => item === undefined)) return true;

        const timeFrom = moment()
            .hours(hours)
            .minutes(minutes);
        const timeTo = moment()
            .hours(otherHours)
            .minutes(otherMinutes);

        if (timeTo.hours() === 12) {
            timeTo.add(1, 'days');
        }

        return moment(timeFrom).isBefore(timeTo, 'minutes');
    }
};

const TimeAfterValidator = {
    getMessage() {
        return 'End time should be after start time';
    },
    validate(value, args) {
        if (args[0].length < 2) return true;

        const [hours, minutes] = value.split(':');
        const [otherHours, otherMinutes] = args[0].split(':');

        if ([otherHours, otherMinutes].some(item => item === undefined)) return true;

        const timeTo = moment()
            .hours(hours)
            .minutes(minutes);
        const timeFrom = moment()
            .hours(otherHours)
            .minutes(otherMinutes);

        return moment(timeTo).isAfter(timeFrom, 'minutes');
    }
};

const textareaValidator = {
    getMessage() {
        return 'May contain latin letters, numbers, whitespaces, hyphens, commas and points only';
    },
    validate(value) {
        return /^[a-zA-Z0-9 ,.'\-]+$/.test(value);
    }
};

const dayPickerValidator = {
    getMessage() {
        return 'Should be chosen at least one day';
    },
    validate(value) {
        return value && value.length && value.some(item => {
            return !!item
        });
    }
};

const passwordNotEqualEmailValidator = {
    getMessage() {
        return 'Your password cannot be similar to the email';
    },
    validate(value, args) {
        const password = value;
        const email = args[0];

        return password.toLowerCase() !== email.toLowerCase();
    }
};

Validator.extend('timeBefore', TimeBeforeValidator, {
    hasTarget: true
});
Validator.extend('timeAfter', TimeAfterValidator, {
    hasTarget: true
});
Validator.extend('bigger-or-equal', BiggerOrEqualNumberValidator);
Validator.extend('current-future-date', CurrentOrFutureDateValidator);
Validator.extend('bigger', BiggerNumberValidator);
Validator.extend('list-item', ListItemValidator);
Validator.extend('percentage', PercentageValidator);
Validator.extend('future-date', FutureDateValidator);
Validator.extend('class-add-on', ClassAddOnValidator);
Validator.extend('class-description', ClassDescriptionValidator);
Validator.extend('list-item', ListItemValidator);
Validator.extend('class-name', ClassNameValidator);
Validator.extend('unique-portal', PortalNameUniqueValidator);
Validator.extend('portal-name', PortalNameValidator);
Validator.extend('latin', latinValidator);
Validator.extend('password', passwordValidator);
Validator.extend('schoolName', schoolNameValidator);
Validator.extend('website', websiteValidator);
Validator.extend('street', streetValidator);
Validator.extend('address', addressValidator);
Validator.extend('tagline', taglineValidator);
Validator.extend('customFormsValidator', customFormsValidator);
Validator.extend('timezone', timeZoneValidator);
Validator.extend('phone', phoneValidator);
Validator.extend('phone-uk', phoneValidatorForEngland);
Validator.extend('unique-email', uniqueEmailValidator);
Validator.extend('expirationDate', expirationDate);
Validator.extend('routing-number', routingNumber);
Validator.extend('account-number', AccountNumberValidator);
Validator.extend('expiry-year', ExpiryYearValidator);
Validator.extend('expiry-month', ExpiryMonthValidator);
Validator.extend('expiry-date-length', ExpiryDateLengthValidator);
Validator.extend('policy', PolicyValidator);
Validator.extend('textarea', textareaValidator);
Validator.extend('checkLatinSymbol', checkLatinSymbolValidator);
Validator.extend('least-one-day', dayPickerValidator);
Validator.extend('not-equal-email', passwordNotEqualEmailValidator);

export default Validator;


// WEBPACK FOOTER //
// ./src/validator.js